import { useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  CheckboxField,
  Modal,
  FormField,
  GridBasePayTable,
  TierEditor,
} from "@common";
import { showApiError, showError, showSuccess } from "@common";

import { PractitionerQuickLookModal } from "@common";

import { useCreatePractitionerByClinic, useVerifyPractitionerName } from "@api/hooks";
import { useNetsuitePractitionerNames } from "@hooks/useNetsuitePractitionerNames";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { safeText } from "@utils/utils";
import { useAuth } from "@api/useAuth";
import { isOrganizerOrAdmin } from "@utils/roles";

import {
  TYPE_OPTIONS,
  STATUS_OPTIONS,
  PAID_BASED_ON_OPTIONS,
  RATE_TYPE_OPTIONS,
  PREFIX_OPTIONS,
  GRID_MAX_ROWS,
  OTHER_PROFIT_MAX_ROWS,
  OTHER_DEDUCTION_MAX_ROWS,
  OTHER_DEDUCTION_MODE_HOURLY,
  OTHER_DEDUCTION_MODE_AMOUNT,
  INITIAL_PRACTITIONER_FORM,
  PRACTITIONER_TEXT_FIELDS,
  PRACTITIONER_CHECKBOX_FIELDS,
  PRACTITIONER_NETSUITE_FIELDS,
  PRACTITIONER_FINANCIAL_FORM_KEYS,
  buildCreatePractitionerPayload,
  buildDisplayName,
  computeGridIsValid,
  computeEffectiveGridRows,
  validatePctField,
  validateEmail,
  validateOtherProfit,
  validateOtherDeduction,
  validateNetsuiteIds,
} from "@utils/practitionerFormUtils";

export default function CreatePractitionerSection({
  clinicId,
  canCreate,
  // Practitioner Lead creates personal/basic profiles only — same field split as
  // the directory edit modal and ProfilePractitionerTab (hideFinancialFields).
  hideFinancialFields = false,
}) {
  const showFinancial = !hideFinancialFields;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("pickType"); // "pickType" | "form"

  const { user } = useAuth();
  const canEditNetsuiteIds = isOrganizerOrAdmin(user);

  const createMut = useCreatePractitionerByClinic();
  const verifyMut = useVerifyPractitionerName();
  const submitting = createMut.isPending;
  const verifying = verifyMut.isPending;

  const [form, setForm] = useState(INITIAL_PRACTITIONER_FORM);

  // Matches surfaced during pre-create verification
  const [verifyMatches, setVerifyMatches] = useState([]);
  const [verifyTotalCount, setVerifyTotalCount] = useState(0);
  // Profile being previewed in the QuickLook modal (null = closed)
  const [previewProfile, setPreviewProfile] = useState(null);

  const pickedType = safeText(form.practitioner_type).trim().toLowerCase();
  const hasPickedType = pickedType.length > 0;

  const canSubmit = useMemo(() => {
    return (
      !!safeText(form.first_name).trim() &&
      !!safeText(form.last_name).trim() &&
      !!safeText(form.practitioner_type).trim()
    );
  }, [form.first_name, form.last_name, form.practitioner_type]);

  const gridIsValid = useMemo(() => computeGridIsValid(form), [form]);

  const effectiveGridRows = useMemo(() => {
    return computeEffectiveGridRows(form);
  }, [form]);

  // ✅ Associate fee % uses same logic as Base Pay %
  const feeMeta = useMemo(
    () => validatePctField(form.associate_fee_percentage),
    [form.associate_fee_percentage],
  );

  const emailMeta = useMemo(() => validateEmail(form.email), [form.email]);

  const otherProfitMeta = useMemo(
    () =>
      validateOtherProfit({
        enabled: !!form.other_profit_enabled,
        rows: form.other_profit_rows,
      }),
    [form.other_profit_enabled, form.other_profit_rows],
  );

  const otherDeductionMeta = useMemo(
    () =>
      validateOtherDeduction({
        enabled: !!form.other_deduction_enabled,
        rows: form.other_deduction_rows,
      }),
    [form.other_deduction_enabled, form.other_deduction_rows],
  );

  const netsuiteMeta = useMemo(() => validateNetsuiteIds(form), [form]);
  // Resolve NetSuite vendor/account names for live "Name (#ID)" hints under
  // each ID input. Only fetched for admin/organizer (endpoints are admin-only).
  const netsuiteNames = useNetsuitePractitionerNames(canEditNetsuiteIds ? form : null);

  function update(key, value) {
    // ✅ prevent objects from entering prefix state
    if (key === "prefix") {
      setForm((prev) => ({ ...prev, prefix: safeText(value).trim() }));
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function reset() {
    setForm(INITIAL_PRACTITIONER_FORM);
    setStep("pickType");
    setVerifyMatches([]);
    setVerifyTotalCount(0);
    setPreviewProfile(null);
  }

  async function handleUseMatchedProfile(match) {
    if (!clinicId) {
      showError("Missing clinic id.");
      return;
    }

    try {
      await createMut.mutateAsync({
        clinicCode: clinicId,
        data: {
          clinic: clinicId,
          profile_id: match.id,
        },
      });
      showSuccess("Profile linked");
      setPreviewProfile(null);
      reset();
      setOpen(false);
    } catch (e) {
      showError(getApiErrorMessage(e));
    }
  }

  function closeModal() {
    if (submitting) return;
    setOpen(false);
  }

  function selectType(type) {
    update("practitioner_type", type);

    // ✅ force prefix into a string immediately
    update("prefix", safeText(form.prefix).trim());

    // If switching to HYG and prefix is Dr., clear it
    if (type === "hyg" && safeText(form.prefix).trim() === "Dr.") {
      update("prefix", "");
    }
  }

  function goToFormStep() {
    if (!hasPickedType) return;

    // ✅ always normalize prefix before moving on
    update("prefix", safeText(form.prefix).trim());

    // DDS should default to Dr.
    if (pickedType === "dds") update("prefix", "Dr.");

    setStep("form");
  }

  function addGridRow() {
    const forced = Number(safeText(form.__grid_rows_forced).trim() || "1");
    const next = Math.min(GRID_MAX_ROWS, forced + 1);
    update("__grid_rows_forced", String(next));
  }

  function validateForm() {
    if (!canCreate) return false;
    if (!clinicId) {
      showError("Missing clinic id.");
      return false;
    }
    if (!canSubmit) {
      showError("First Name, Last Name, and Practitioner Type are required.");
      return false;
    }
    const safeForm = { ...form, prefix: safeText(form.prefix).trim() };
    if (!buildDisplayName(safeForm)) {
      showError("Please enter a name.");
      return false;
    }
    if (showFinancial && safeForm.grid && !gridIsValid) {
      showError("Please enter valid Base Pay (≥ 0) and % (0–100).");
      return false;
    }
    if (showFinancial && !feeMeta.ok) {
      showError("Associate fee percentage must be a valid number between 0 and 100.");
      return false;
    }
    if (!emailMeta.ok) {
      showError("Please enter a valid email address.");
      return false;
    }
    if (showFinancial && !otherProfitMeta.ok) {
      showError(
        "Each Other Profit tier needs a label and a percentage greater than 0 and less than 100.",
      );
      return false;
    }
    if (showFinancial && !otherDeductionMeta.ok) {
      showError(
        "Each Other Deduction tier needs a label and a percentage greater than 0 and less than 100.",
      );
      return false;
    }
    if (canEditNetsuiteIds && !netsuiteMeta.ok) {
      showError("NetSuite IDs must be whole numbers greater than 0.");
      return false;
    }
    return true;
  }

  async function doCreate() {
    const safeForm = { ...form, prefix: safeText(form.prefix).trim() };
    // Strip NetSuite IDs from the payload for non-admin users so they don't
    // accidentally send default empty values that would clear server-side data.
    const formForPayload = (() => {
      const cleaned = { ...safeForm };
      if (!canEditNetsuiteIds) {
        for (const { key } of PRACTITIONER_NETSUITE_FIELDS) cleaned[key] = "";
      }
      // Financial inputs were never rendered for this role — send the untouched
      // defaults so a hidden field can't carry a value into the new record.
      if (hideFinancialFields) {
        for (const key of PRACTITIONER_FINANCIAL_FORM_KEYS) {
          cleaned[key] = INITIAL_PRACTITIONER_FORM[key];
        }
      }
      return cleaned;
    })();
    const payload = buildCreatePractitionerPayload({ clinicId, form: formForPayload });
    try {
      await createMut.mutateAsync({ clinicCode: clinicId, data: payload });
      showSuccess("Profile created");
      reset();
      setOpen(false);
    } catch (e) {
      showApiError(e);
    }
  }

  // Called when user clicks "Create" on the form step:
  // 1. Validate form
  // 2. Run verify — if matches found, show the verify step
  // 3. If no matches (or verify fails), create directly
  async function handleCreate() {
    if (!validateForm()) return;

    const firstName = safeText(form.first_name).trim();
    try {
      const result = await verifyMut.mutateAsync(firstName);
      if (result.count > 0) {
        setVerifyMatches(result.matches);
        setVerifyTotalCount(Number(result.total_count) || result.matches.length);
        setStep("verify");
        return;
      }
    } catch {
      // If verify call fails, fall through and create anyway
    }

    await doCreate();
  }

  if (!canCreate) return null;

  return (
    <div className="mb-5 flex items-center justify-between gap-4">
      <Button
        onClick={() => {
          setOpen(true);
          reset();
        }}
      >
        Create New Profile
      </Button>

      <Modal
        isOpen={open}
        onClose={closeModal}
        title={step === "verify" ? "Matching Profiles Found" : "Create New Profile"}
        size="lg"
        footer={
          <>
            {step === "verify" ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep("form");
                    setVerifyMatches([]);
                    setVerifyTotalCount(0);
                  }}
                  disabled={submitting}
                >
                  Back to form
                </Button>
                <Button onClick={doCreate} disabled={submitting}>
                  {submitting ? "Creating..." : "Create new profile anyway"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={closeModal} disabled={submitting || verifying}>
                  Cancel
                </Button>
                {step === "pickType" ? (
                  <Button onClick={goToFormStep} disabled={!hasPickedType || submitting}>
                    Continue
                  </Button>
                ) : (
                  <Button
                    onClick={handleCreate}
                    disabled={
                      !canSubmit ||
                      submitting ||
                      verifying ||
                      (showFinancial && form.grid && !gridIsValid) ||
                      (showFinancial && !feeMeta.ok) ||
                      !emailMeta.ok ||
                      (showFinancial && !otherProfitMeta.ok) ||
                      (showFinancial && !otherDeductionMeta.ok) ||
                      (canEditNetsuiteIds && !netsuiteMeta.ok)
                    }
                  >
                    {verifying ? "Checking..." : submitting ? "Creating..." : "Create"}
                  </Button>
                )}
              </>
            )}
          </>
        }
      >
        <div className="text-xs text-palette-gray-secondary mb-4">Clinic: {clinicId}</div>

        {step === "pickType" && (
          <div className="rounded-xl border border-palette-gray-divider p-4">
            <div className="text-sm font-semibold text-palette-slate-primary mb-2">
              Select practitioner type
            </div>
            <div className="text-xs text-palette-gray-secondary mb-4">
              Choose the practitioner type to continue.
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {TYPE_OPTIONS.filter((o) => o.value !== "").map((o) => (
                <Button
                  key={o.value}
                  variant={pickedType === o.value ? "primary" : "outline"}
                  onClick={() => selectType(o.value)}
                  disabled={submitting}
                  className={pickedType === o.value ? "" : "bg-white"}
                >
                  {o.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {step === "form" && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Dropdown
              label="Prefix"
              value={safeText(form.prefix)}
              onChange={(v) => update("prefix", v)}
              options={PREFIX_OPTIONS}
              disabled={submitting}
            />

            <div className="hidden sm:block" />

            {PRACTITIONER_TEXT_FIELDS.map((f) => (
              <div key={f.key} className={f.colSpan === 2 ? "sm:col-span-2" : ""}>
                <FormField
                  label={f.label}
                  name={f.key}
                  value={form[f.key]}
                  onChange={(e) => update(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  required={!!f.required}
                  disabled={submitting || verifying}
                  showError={false}
                />
              </div>
            ))}

            {/* Rate Type and Hire Date use non-text inputs so they're rendered explicitly */}
            <Dropdown
              label="Rate Type"
              value={form.rate_type}
              onChange={(v) => update("rate_type", v)}
              options={RATE_TYPE_OPTIONS}
              disabled={submitting}
            />

            <FormField
              label="Hire Date"
              name="hire_date"
              type="date"
              value={form.hire_date}
              onChange={(e) => update("hire_date", e.target.value)}
              disabled={submitting}
              showError={false}
            />

            <FormField
              label="Supervisor"
              name="supervisor"
              value={form.supervisor ?? ""}
              onChange={(e) => update("supervisor", e.target.value)}
              placeholder="e.g. Katchikian, Sherry"
              disabled={submitting}
            />

            {/* Payment / compensation fields below — hidden for Practitioner Lead */}
            {showFinancial && (
              <div className="sm:col-span-2">
                <Dropdown
                  label="Paid Based On"
                  value={form.paid_based_on}
                  onChange={(v) => update("paid_based_on", v)}
                  options={PAID_BASED_ON_OPTIONS}
                  disabled={submitting}
                />
              </div>
            )}

            <Dropdown
              label="Status"
              value={form.status}
              onChange={(v) => update("status", v)}
              options={STATUS_OPTIONS}
              disabled={submitting}
            />

            {showFinancial && (
              <div className="sm:col-span-2">
                <FormField
                  label="Associate Fee (%)"
                  name="associate_fee_percentage"
                  value={form.associate_fee_percentage}
                  onChange={(e) => update("associate_fee_percentage", e.target.value)}
                  placeholder="e.g. 40%"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  disabled={submitting}
                  showError={false}
                />
                {!feeMeta.ok && (
                  <div className="mt-1 text-xs text-palette-red-badge">
                    Associate fee percentage must be a valid number between 0 and 100.
                  </div>
                )}
              </div>
            )}

            {showFinancial && (
              <div className="sm:col-span-2">
                <FormField
                  label="Insurance"
                  name="insurance"
                  value={form.insurance}
                  onChange={(e) => update("insurance", e.target.value)}
                  placeholder="Optional"
                  type="number"
                  step="0.01"
                  disabled={submitting}
                  showError={false}
                />
              </div>
            )}

            {/* ✅ checkboxes using CheckboxField — all compensation-related */}
            {showFinancial && (
              <div className="sm:col-span-2 flex flex-wrap gap-6 pt-2">
                {PRACTITIONER_CHECKBOX_FIELDS.map((c) => (
                  <CheckboxField
                    key={c.key}
                    label={c.label}
                    checked={!!form[c.key]}
                    onChange={(val) => update(c.key, val)}
                    disabled={submitting}
                  />
                ))}
                <CheckboxField
                  label="Other Profit"
                  checked={!!form.other_profit_enabled}
                  onChange={(val) => update("other_profit_enabled", val)}
                  disabled={submitting}
                />
                <CheckboxField
                  label="Other Deduction"
                  checked={!!form.other_deduction_enabled}
                  onChange={(val) => update("other_deduction_enabled", val)}
                  disabled={submitting}
                />
              </div>
            )}

            {/* TierEditor replaces two ~80-line near-identical inline blocks for Other Profit
                and Other Deduction tiers. The only differences (title, field names, input
                constraints, placeholders, error messages) are passed as props. */}
            {form.other_profit_enabled && (
              <TierEditor
                title="Other Profit Tiers"
                rows={form.other_profit_rows}
                rowsVisible={form.__other_profit_rows_visible}
                maxRows={OTHER_PROFIT_MAX_ROWS}
                valueField="percent"
                valueInputProps={{ type: "number", min: "0", max: "100", step: "0.01" }}
                valuePlaceholder="%"
                labelPlaceholder="Label (e.g. Referral Bonus)"
                emptyRow={{ label: "", percent: "" }}
                errorOk={otherProfitMeta.ok}
                errorMessage="Each tier needs a label and a percentage greater than 0 and up to 100."
                note="The percentage here is always positive. On the monthly report, enter a negative number if the amount should be deducted."
                submitting={submitting}
                onRowsChange={(rows) => update("other_profit_rows", rows)}
                onVisibleChange={(n) => update("__other_profit_rows_visible", n)}
              />
            )}

            {form.other_deduction_enabled && (
              <TierEditor
                title="Other Deduction Tiers"
                rows={form.other_deduction_rows}
                rowsVisible={form.__other_deduction_rows_visible}
                maxRows={OTHER_DEDUCTION_MAX_ROWS}
                valueField="hour_rate"
                valueInputProps={{ type: "number", min: "0", step: "0.01" }}
                valuePlaceholder="$/hr"
                labelPlaceholder="Label (e.g. Lab Share)"
                emptyRow={{ label: "", mode: OTHER_DEDUCTION_MODE_HOURLY, hour_rate: "" }}
                modeField="mode"
                modeOptions={[
                  { value: OTHER_DEDUCTION_MODE_HOURLY, label: "Hourly" },
                  { value: OTHER_DEDUCTION_MODE_AMOUNT, label: "Amount" },
                ]}
                valueHiddenModes={[OTHER_DEDUCTION_MODE_AMOUNT]}
                valueHiddenNote="Entered monthly"
                errorOk={otherDeductionMeta.ok}
                errorMessage="Each tier needs a label, and an hourly tier also needs a non-negative hour rate."
                submitting={submitting}
                onRowsChange={(rows) => update("other_deduction_rows", rows)}
                onVisibleChange={(n) => update("__other_deduction_rows_visible", n)}
              />
            )}

            {form.per_day && (
              <div className="sm:col-span-2">
                <FormField
                  label="Per Day Value"
                  name="per_day_value"
                  value={form.per_day_value}
                  onChange={(e) => update("per_day_value", e.target.value)}
                  placeholder="Optional"
                  type="number"
                  step="0.01"
                  disabled={submitting}
                  showError={false}
                />
              </div>
            )}

            {form.grid && (
              <GridBasePayTable
                form={form}
                update={update}
                submitting={submitting}
                effectiveGridRows={effectiveGridRows}
                addGridRow={addGridRow}
                gridIsValid={gridIsValid}
              />
            )}

            {canEditNetsuiteIds && (
              <div className="sm:col-span-2 space-y-3 rounded-xl border border-palette-gray-divider p-3">
                <div className="text-sm font-semibold text-palette-slate-primary">NetSuite IDs</div>
                <div className="text-xs text-palette-gray-secondary">
                  Admin only. Each ID must be a whole number greater than 0.
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {PRACTITIONER_NETSUITE_FIELDS.map((f) => {
                    const meta = netsuiteMeta.fields[f.key];
                    const invalid = meta?.hasAny && !meta?.ok;
                    const resolvedName = netsuiteNames.getName(f.key, form[f.key]);
                    const hasId = !!safeText(form[f.key]).trim();
                    const pending = netsuiteNames.isPendingFor(f.key, form[f.key]);
                    return (
                      <div key={f.key}>
                        <FormField
                          label={f.label}
                          name={f.key}
                          value={form[f.key]}
                          onChange={(e) => {
                            const raw = String(e.target.value).replace(/[^\d]/g, "");
                            update(f.key, raw);
                          }}
                          placeholder="Optional"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          disabled={submitting}
                          showError={false}
                        />
                        {invalid ? (
                          <div className="mt-1 text-xs text-palette-red-badge">
                            Must be a whole number greater than 0.
                          </div>
                        ) : hasId ? (
                          <div className="mt-1 text-xs">
                            {pending ? (
                              <span className="text-palette-gray-secondary">Looking up name…</span>
                            ) : resolvedName ? (
                              <span className="font-semibold text-palette-indigo-heading">
                                {resolvedName}
                              </span>
                            ) : (
                              <span className="italic text-palette-amber-badge">
                                No matching NetSuite record found.
                              </span>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {step === "verify" && (
          <div className="space-y-4">
            <div className="rounded-xl border border-palette-amber-border bg-palette-amber-soft px-4 py-3">
              <div className="text-sm font-semibold text-palette-amber-primary">
                {verifyTotalCount} existing profile{verifyTotalCount !== 1 ? "s" : ""} found
                matching &ldquo;{safeText(form.first_name)}&rdquo;
              </div>
              <div className="mt-1 text-xs text-palette-amber-heading">
                Select a suggestion to pre-fill the form, or use the create button below to make a
                new profile.
              </div>
            </div>

            <div className="space-y-2">
              {verifyMatches.map((match) => (
                <div
                  key={match.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-palette-gray-divider bg-white px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-palette-slate-primary truncate">
                      {match.name ||
                        [match.prefix, match.first_name, match.last_name]
                          .filter(Boolean)
                          .join(" ") ||
                        "—"}
                    </div>
                    {match.actual_name && (
                      <div className="text-xs text-palette-gray-secondary truncate">
                        Corp: {match.actual_name}
                      </div>
                    )}
                    {match.email && (
                      <div className="text-xs text-palette-gray-placeholder truncate">
                        {match.email}
                      </div>
                    )}
                    {match.practitioner_type && (
                      <div className="text-xs text-palette-indigo-label uppercase font-medium mt-0.5">
                        {match.practitioner_type}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setPreviewProfile(match)}>
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Read-only preview of a matched profile — stacks on top of the create modal */}
      <PractitionerQuickLookModal
        isOpen={!!previewProfile}
        onClose={() => setPreviewProfile(null)}
        practitioner={previewProfile}
        canEdit={false}
        onSave={null}
        fallbackClinicCode={clinicId}
        highlightClinicBadgesTop
        showUseThisButton
        onUseThis={handleUseMatchedProfile}
        isUsingThis={submitting}
      />
    </div>
  );
}
